import React, { useEffect, useState } from "react";
import { Plus, Pencil, Search, Boxes, Trash2, Package, Upload, X, History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Panel } from "../../lib/ui";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { SetStockDialog } from "./Fabrics";
import { AdjustmentHistoryDialog } from "./AdjustmentHistory";

const num = (n) => (Number(n) || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const emptyPack = () => ({ pcs_per_pack: "", available: 0, production: 0, remaining: 0 });

export default function Accessories() {
  const { can, user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("all");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [stockFor, setStockFor] = useState(null);
  const [histFor, setHistFor] = useState(null);
  const [legacyConversion, setLegacyConversion] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const write = can("production_admin", "warehouse_admin", "rnd");

  const load = () => api.get("/production/accessories", { params: { q: q || undefined, active: active === "all" ? undefined : active } }).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active]);
  useEffect(() => { api.get("/warehouses", { params: { usage: "material" } }).then((r) => setWarehouses(r.data)); const t = setTimeout(load, 350); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const [page, setPage] = useState(1);
  const pageSize = 20;
  useEffect(() => { setPage(1); }, [q, active]);
  const total = list.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, totalPages);
  const paged = list.slice((curPage - 1) * pageSize, curPage * pageSize);

  return (
    <div data-testid="accessories-page">
      <PageHeader title="Stok Aksesori" subtitle="Master aksesori: Name, Harga @Pcs, Foto & Pack Type (Available/Production/Remaining)">
        {write && <Button onClick={() => { setEdit(null); setOpen(true); }} data-testid="add-accessory-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory"><Plus className="w-4 h-4 mr-1.5" /> Aksesori Baru</Button>}
      </PageHeader>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / kode..." className="pl-9 bg-white" data-testid="accessory-search" />
        </div>
        <Select value={active} onValueChange={setActive}>
          <SelectTrigger className="w-40 bg-white" data-testid="accessory-active-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="active">Aktif</SelectItem><SelectItem value="inactive">Nonaktif</SelectItem></SelectContent>
        </Select>
      </div>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Foto</TableHead><TableHead>Kode</TableHead><TableHead>Nama</TableHead>
              <TableHead className="text-right">Harga/Pcs</TableHead><TableHead className="text-right">Available</TableHead>
              <TableHead className="text-right">Production</TableHead><TableHead className="text-right">Remaining</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paged.map((a) => (
              <TableRow key={a.id} data-testid={`accessory-row-${a.id}`}>
                <TableCell>{a.photo ? <img src={a.photo} alt="" className="w-9 h-9 rounded object-cover border border-taupe/30" /> : <div className="w-9 h-9 rounded bg-taupe/20" />}</TableCell>
                <TableCell className="font-mono text-xs">{a.code}</TableCell>
                <TableCell className="font-semibold text-cocoa">{a.name}</TableCell>
                <TableCell className="text-right">{fmtIDR(a.price_per_pcs)}</TableCell>
                <TableCell className="text-right font-semibold text-cocoa">{num(a.available_stock)}</TableCell>
                <TableCell className="text-right">{num(a.production_stock)}</TableCell>
                <TableCell className="text-right">{num(a.remaining_stock)}</TableCell>
                <TableCell><span className={a.active ? "text-status-passed" : "text-wmuted"}>{a.active ? "Aktif" : "Nonaktif"}</span></TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  {write && <>
                    <Button size="icon" variant="ghost" title="Set Stok" onClick={() => setStockFor({ id: a.id, name: a.name, packs: a.packs })} data-testid={`accessory-setstock-${a.id}`}><Boxes className="w-4 h-4 text-cocoa" /></Button>
                    {user?.role === "owner" && a.source_system === "LEGACY_MFG" && <Button size="icon" variant="ghost" title="Konversi saldo pack legacy ke pcs" onClick={() => setLegacyConversion(a)} data-testid={`accessory-legacy-pack-convert-${a.id}`}><RefreshCw className="w-4 h-4 text-status-passed" /></Button>}
                    <Button size="icon" variant="ghost" title="Riwayat Penyesuaian" onClick={() => setHistFor({ material_id: a.id, name: a.name, kind: "accessory" })} data-testid={`accessory-history-${a.id}`}><History className="w-4 h-4 text-cocoa" /></Button>
                    <Button size="icon" variant="ghost" title="Edit" onClick={() => { setEdit(a); setOpen(true); }} data-testid={`accessory-edit-${a.id}`}><Pencil className="w-4 h-4 text-cocoa" /></Button>
                    <Button size="icon" variant="ghost" title="Hapus" onClick={() => delAcc(a, load)} data-testid={`accessory-del-${a.id}`}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
            {!total && <TableRow><TableCell colSpan={9} className="text-center text-wmuted py-10"><Package className="w-6 h-6 mx-auto mb-2 opacity-50" />Belum ada data aksesori.</TableCell></TableRow>}
          </TableBody>
        </Table>
        {total > pageSize && (
          <div className="flex items-center justify-between px-3 py-3 border-t border-taupe/15 text-xs text-wmuted" data-testid="accessory-pagination">
            <span>Menampilkan {(curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, total)} dari {total}</span>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="outline" className="border-taupe h-8" disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="accessory-prev">‹</Button>
              <span className="px-2 text-cocoa">Hal {curPage}/{totalPages}</span>
              <Button size="sm" variant="outline" className="border-taupe h-8" disabled={curPage >= totalPages} onClick={() => setPage(curPage + 1)} data-testid="accessory-next">›</Button>
            </div>
          </div>
        )}
      </Panel>

      <AccessoryDialog open={open} setOpen={setOpen} edit={edit} warehouses={warehouses} onDone={load} />
      <SetStockDialog item={stockFor} setItem={setStockFor} kind="accessory" warehouses={warehouses} onDone={load} />
      <AdjustmentHistoryDialog item={histFor} setItem={setHistFor} warehouses={warehouses} onDone={load} />
      <LegacyPackConversionDialog item={legacyConversion} setItem={setLegacyConversion} onDone={load} />
    </div>
  );
}

function LegacyPackConversionDialog({ item, setItem, onDone }) {
  const [preview, setPreview] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!item) return;
    setPreview(null);
    setConfirmed(false);
    setBusy(true);
    api.post(`/production/accessories/${item.id}/legacy-pack-conversion`, { confirm: false })
      .then((response) => setPreview(response.data))
      .catch((error) => toast.error(formatApiError(error.response?.data?.detail)))
      .finally(() => setBusy(false));
  }, [item]);

  const convert = async () => {
    if (!item || !confirmed) return;
    setBusy(true);
    try {
      const response = await api.post(
        `/production/accessories/${item.id}/legacy-pack-conversion`,
        { confirm: true },
      );
      toast.success(`${response.data.rows.length} saldo pack dikonversi ke pcs`);
      setItem(null);
      onDone();
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!item) return null;
  return (
    <Dialog open onOpenChange={() => setItem(null)}>
      <DialogContent className="max-w-2xl" data-testid="legacy-pack-conversion-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-cocoa">Konversi Saldo Pack Legacy</DialogTitle>
          <DialogDescription className="text-xs text-wmuted">
            {item.name}: saldo lama yang masih tercatat sebagai jumlah pack akan dikonversi
            menjadi pcs berdasarkan Pcs/Pack master. Stok yang sudah berubah akan dilewati.
          </DialogDescription>
        </DialogHeader>
        {busy && !preview ? (
          <div className="py-8 text-center text-sm text-wmuted">Memeriksa saldo legacy...</div>
        ) : (
          <div className="space-y-3">
            <div className="overflow-x-auto border border-taupe/20 rounded-lg">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Jenis</TableHead><TableHead className="text-right">Saldo Lama</TableHead><TableHead className="text-right">Pcs/Pack</TableHead><TableHead className="text-right">Target Pcs</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(preview?.rows || []).map((row, index) => <TableRow key={`${row.bucket}-${row.lot}`} data-testid={`legacy-pack-row-${index}`}><TableCell>{row.bucket}</TableCell><TableCell className="text-right">{num(row.current_qty)} pack</TableCell><TableCell className="text-right">{num(row.pcs_per_pack)}</TableCell><TableCell className="text-right font-semibold text-cocoa">{num(row.target_qty)} pcs</TableCell><TableCell className={row.can_convert ? "text-status-passed" : "text-wmuted"}>{row.status}</TableCell></TableRow>)}
                  {!preview?.rows?.length && <TableRow><TableCell colSpan={5} className="text-center py-6 text-wmuted">Tidak ada saldo pack legacy yang perlu diperiksa.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
            {preview?.stock_will_change && <label className="flex items-start gap-2 text-xs text-cocoa cursor-pointer"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} data-testid="legacy-pack-confirm" />Saya sudah memastikan jumlah pack dan Pcs/Pack. Konversi akan mengoreksi saldo material menjadi pcs serta membuat audit adjustment.</label>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setItem(null)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={convert} disabled={busy || !confirmed} data-testid="legacy-pack-convert-confirm" className="bg-status-passed text-white">Konversi ke Pcs</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function delAcc(a, cb) {
  if (!window.confirm(`Hapus aksesori ${a.name}?`)) return;
  try { const r = await api.delete(`/production/accessories/${a.id}`); toast.success(r.data.soft ? "Aksesori dinonaktifkan (masih ada stok)" : "Aksesori dihapus"); cb(); }
  catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
}

function AccessoryDialog({ open, setOpen, edit, warehouses, onDone }) {
  const [form, setForm] = useState({ name: "", code: "", price_per_pcs: 0, image: null, supplier_id: "", warehouse_id: "", active: true, packs: [emptyPack()] });
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/suppliers", { params: { active: "active", partner_type: "supplier_aksesori" } }).then((r) => setSuppliers(r.data));
    if (edit) api.get(`/production/accessories/${edit.id}`).then((r) => setForm({ name: r.data.name, code: r.data.code, price_per_pcs: r.data.price_per_pcs, image: r.data.photo, supplier_id: r.data.supplier_id || "", warehouse_id: "", active: r.data.active, packs: (r.data.packs || []).length ? r.data.packs : [emptyPack()] }));
    else setForm({ name: "", code: "", price_per_pcs: 0, image: null, supplier_id: "", warehouse_id: "", active: true, packs: [emptyPack()] });
  }, [open, edit]);

  const setPack = (i, patch) => setForm((f) => ({ ...f, packs: f.packs.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  const onImage = (file) => { if (!file) return; if (file.size > 500 * 1024) return toast.error("Ukuran gambar maksimal 500kb"); const r = new FileReader(); r.onload = () => setForm((f) => ({ ...f, image: r.result })); r.readAsDataURL(file); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama aksesori wajib diisi");
    const payload = { name: form.name, code: form.code || null, price_per_pcs: parseFloat(form.price_per_pcs) || 0, image: form.image, supplier_id: form.supplier_id || null, warehouse_id: form.warehouse_id || null, active: form.active,
      packs: form.packs.filter((p) => p.pcs_per_pack || p.available || p.production || p.remaining).map((p) => ({ pcs_per_pack: String(p.pcs_per_pack), available: parseFloat(p.available) || 0, production: parseFloat(p.production) || 0, remaining: parseFloat(p.remaining) || 0 })) };
    try {
      if (edit) await api.put(`/production/accessories/${edit.id}`, payload);
      else await api.post("/production/accessories", payload);
      toast.success("Aksesori disimpan"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">{edit ? "Edit" : "Buat"} Aksesori</DialogTitle></DialogHeader>

        <div className="border border-taupe/20 rounded-lg p-4">
          <div className="text-sm font-semibold text-cocoa mb-3">Informasi Aksesori</div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Nama *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama aksesori" data-testid="accessory-name" /></div>
            <div><Label>Kode</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Otomatis" data-testid="accessory-code" /></div>
            <div><Label>Harga @Pcs</Label><Input type="number" value={form.price_per_pcs} onChange={(e) => setForm({ ...form, price_per_pcs: e.target.value })} data-testid="accessory-price" /></div>
            <div><Label>Supplier Aksesori</Label>
              <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="accessory-supplier"><SelectValue placeholder="Pilih" /></SelectTrigger>
                <SelectContent><SelectItem value="none">- Tanpa Supplier -</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {!edit && <div><Label>Gudang Stok Awal</Label>
              <Select value={form.warehouse_id || "none"} onValueChange={(v) => setForm({ ...form, warehouse_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="accessory-warehouse"><SelectValue placeholder="Pilih gudang" /></SelectTrigger>
                <SelectContent><SelectItem value="none">- Pilih -</SelectItem>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>}
          </div>
          <div className="mt-3">
            <Label>Foto Aksesori</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.image ? <img src={form.image} alt="" className="w-16 h-16 rounded object-cover border border-taupe/30" /> : <div className="w-16 h-16 rounded bg-taupe/20 flex items-center justify-center"><Upload className="w-5 h-5 text-wmuted" /></div>}
              <input type="file" accept="image/png,image/jpeg" onChange={(e) => onImage(e.target.files[0])} className="text-xs" data-testid="accessory-image" />
              {form.image && <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, image: null })} className="text-status-rejected">Hapus</Button>}
            </div>
            <p className="text-[11px] text-wmuted mt-1">JPG/PNG, maks 500kb.</p>
          </div>
        </div>

        <div className="border border-taupe/20 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-cocoa">Packs & Stock</div>
            <Button variant="outline" size="sm" className="border-taupe text-cocoa" onClick={() => setForm((f) => ({ ...f, packs: [...f.packs, emptyPack()] }))} data-testid="add-pack-btn"><Plus className="w-3.5 h-3.5 mr-1" />Tambah Pack Type</Button>
          </div>
          <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-xs text-wmuted px-1"><span>Pcs / Pack</span><span>Available</span><span>Production</span><span>Remaining</span><span></span></div>
          {form.packs.map((p, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 mt-1 items-center">
              <Input placeholder="mis. 100" value={p.pcs_per_pack} onChange={(e) => setPack(i, { pcs_per_pack: e.target.value })} data-testid={`pack-pcs-${i}`} />
              <Input type="number" value={p.available} onChange={(e) => setPack(i, { available: e.target.value })} data-testid={`pack-avail-${i}`} />
              <Input type="number" value={p.production} onChange={(e) => setPack(i, { production: e.target.value })} />
              <Input type="number" value={p.remaining} onChange={(e) => setPack(i, { remaining: e.target.value })} />
              <Button size="icon" variant="ghost" onClick={() => setForm((f) => ({ ...f, packs: f.packs.filter((_, idx) => idx !== i) }))}><Trash2 className="w-4 h-4 text-status-rejected" /></Button>
            </div>
          ))}
          {edit && <p className="text-[11px] text-wmuted mt-1">Catatan: saat edit, stok diubah lewat tombol <b>Set Stok</b>.</p>}
        </div>

        <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Aktif</Label></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} data-testid="save-accessory-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">Simpan Aksesori</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
